import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { api, useAuth } from '../App';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, Save, Send, Loader2, ExternalLink, AlertTriangle } from 'lucide-react';
import { stripeErrorToast } from '../utils/stripeErrors';
import { SERVICE_TEMPLATES } from '../lib/serviceTemplates';

import { StepProgress } from '../components/onboarding/StepProgress';
import { BasicInfoStep } from '../components/onboarding/BasicInfoStep';
import { ServiceTypeStep } from '../components/onboarding/ServiceTypeStep';
import { DepositStep } from '../components/onboarding/DepositStep';
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from '../components/ui/alert-dialog';
import { CategoriesStep } from '../components/onboarding/CategoriesStep';
import { ServicesStep } from '../components/onboarding/ServicesStep';
import { PayoutStep } from '../components/onboarding/PayoutStep';
import { ReviewStep } from '../components/onboarding/ReviewStep';

const STEPS = [
  { number: 1, title: 'Basic Info' },
  { number: 2, title: 'Service Type' },
  { number: 3, title: 'Deposit' },
  { number: 4, title: 'Categories' },
  { number: 5, title: 'Services' },
  { number: 6, title: 'Payouts' },
  { number: 7, title: 'Review' }
];

export default function ProviderOnboarding() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [payoutStatus, setPayoutStatus] = useState(null);
  const [skipPayoutOpen, setSkipPayoutOpen] = useState(false);
  const [startingConnect, setStartingConnect] = useState(false);
  // Set to true once fetchExistingProfile has run, used to gate
  // the URL-param pre-tick so the profile fetch can't clobber it.
  const [profileLoaded, setProfileLoaded] = useState(false);
  
  const [form, setForm] = useState({
    brand_name: user?.name || '',
    username: '',
    bio: '',
    location: '',
    postcode: '',
    full_address: { line1: '', line2: '', city: '', postcode: '' },
    service_types: [],
    salon_address: { line1: '', line2: '', city: '', postcode: '' },
    home_address: { line1: '', line2: '', city: '', postcode: '' },
    show_full_home_address: false,
    service_radius: null,
    free_travel: false,
    free_travel_miles: 0,
    travel_charges: [],
    categories: [],
    specialties: [],
    portfolio_images: [],
    social_links: { instagram: '', tiktok: '' },
    draft_services: [],
    cancellation_policy: 'flexible',
    default_deposit_percent: null,
  });
  const [usernameStatus, setUsernameStatus] = useState(null);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchExistingProfile(); }, []);

  const fetchExistingProfile = async () => {
    try {
      const response = await api.get('/providers/me');
      if (response.data) {
        const data = response.data;
        setForm({
          brand_name: data.brand_name || '',
          username: data.username || '',
          bio: data.bio || '',
          location: data.location || '',
          postcode: data.postcode || '',
          full_address: data.full_address || { line1: '', line2: '', city: '', postcode: '' },
          service_types: data.service_types || [],
          salon_address: typeof data.salon_address === 'object' ? data.salon_address : (data.salon_address ? { line1: data.salon_address, line2: '', city: '', postcode: '' } : { line1: '', line2: '', city: '', postcode: '' }),
          home_address: typeof data.home_address === 'object' ? data.home_address : (data.home_address ? { line1: data.home_address, line2: '', city: '', postcode: '' } : { line1: '', line2: '', city: '', postcode: '' }),
          show_full_home_address: data.show_full_home_address || false,
          service_radius: data.service_radius || null,
          free_travel: data.free_travel || false,
          free_travel_miles: typeof data.free_travel_miles === 'number' ? data.free_travel_miles : 0,
          travel_charges: data.travel_charges || [],
          categories: data.categories || [],
          specialties: data.specialties || [],
          portfolio_images: data.portfolio_images || [],
          social_links: data.social_links || { instagram: '', tiktok: '', twitter: '', facebook: '' },
          draft_services: data.draft_services || [],
          cancellation_policy: data.cancellation_policy?.type || 'flexible',
          default_deposit_percent:
            typeof data.default_deposit_percent === 'number' ? data.default_deposit_percent : null,
        });
        if (data.username) setUsernameStatus('available');

        // Resume priority:
        //   1. Exact step the user was on when they saved (new field on
        //      draft). Works even if they saved mid-field with nothing
        //      completed yet, which is exactly when heuristics fail.
        //   2. Legacy heuristic below (for older drafts saved before this
        //      field existed).
        //   3. ?stripe_onboarding= override, which always wins (they've
        //      just come back from the Stripe hosted flow and we want to
        //      land them on the Payouts step regardless of draft state).
        const savedStep = Number.isInteger(data.onboarding_step) ? data.onboarding_step : null;
        if (savedStep && savedStep >= 1 && savedStep <= 7) {
          setStep(savedStep);
        } else {
          // Legacy heuristic, only runs when no saved step exists.
          const socials = data.social_links || {};
          const hasSocial = Object.values(socials).some(v => v && v.trim());
          const step1Done = data.brand_name && data.username && data.bio && data.postcode && hasSocial;

          const types = data.service_types || [];
          const salonOk = !types.includes('salon') || (typeof data.salon_address === 'object' ? data.salon_address?.line1 : data.salon_address);
          const homeOk = !types.includes('home') || (typeof data.home_address === 'object' ? data.home_address?.line1 : data.home_address);
          const mobileOk = !types.includes('mobile') || data.service_radius;
          const step2Done = types.length > 0 && salonOk && homeOk && mobileOk;

          const step3Done = (data.categories || []).length > 0;

          // Step 5 (services) is optional, skip to review if categories are done.
          // The Deposit step (step 3) and Payout step (step 6) are always optional.
          const step5Done = (data.draft_services || []).length > 0;

          if (step1Done && step2Done && step3Done && step5Done) {
            setStep(7); // Review
          } else if (step1Done && step2Done && step3Done) {
            setStep(5); // Services
          } else if (step1Done && step2Done) {
            setStep(4); // Categories (Deposit step 3 is optional so we skip past it)
          } else if (step1Done) {
            setStep(2);
          }
          // else stay on step 1
        }

        // If returning from Stripe hosted onboarding, jump straight back to
        // the Payouts step so the confirmation screen renders there (not the
        // resume-logic default).
        const returnParams = new URLSearchParams(window.location.search);
        if (returnParams.get('stripe_onboarding')) {
          setStep(6);
        }
      }
    } catch (err) { console.error('Failed to fetch existing profile:', err); }
    finally { setProfileLoaded(true); }
  };

  // Debounced username check
  useEffect(() => {
    const cleaned = form.username.replace(/^@/, '').toLowerCase();
    if (cleaned.length < 3) {
      setUsernameStatus(cleaned.length > 0 ? 'invalid' : null);
      return;
    }
    setUsernameStatus('checking');
    const timer = setTimeout(async () => {
      try {
        const params = user?.user_id ? `?exclude_user=${user.user_id}` : '';
        const res = await api.get(`/providers/check-username/${cleaned}${params}`);
        setUsernameStatus(res.data.available ? 'available' : 'taken');
      } catch (err) { console.error('Username check error:', err); setUsernameStatus('invalid'); }
    }, 400);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.username, user?.user_id]);

  // Pre-tick a category if the user landed from a vertical funnel
  // (e.g. /for-barbers → /register?category=barbering_grooming). Gated
  // on `profileLoaded` so the profile fetch can't clobber the pre-tick
  // by overwriting form state a moment after we set it. Only fires once
  // the URL param resolves to a real pillar id AND the provider doesn't
  // already have that pillar selected (so resuming a draft doesn't
  // double-stamp the templates).
  useEffect(() => {
    if (!profileLoaded) return;
    const requested = searchParams.get('category');
    if (!requested) return;
    if (form.categories.includes(requested)) return;
    import('../lib/categories').then(({ CATEGORY_IDS }) => {
      if (CATEGORY_IDS.includes(requested)) {
        handleCategoryToggle(requested);
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileLoaded, searchParams.get('category')]);

  const handleServiceTypeToggle = (typeId) => {
    setForm(prev => ({
      ...prev,
      service_types: prev.service_types.includes(typeId)
        ? prev.service_types.filter(t => t !== typeId)
        : [...prev.service_types, typeId]
    }));
  };

  const handleCategoryToggle = (categoryId) => {
    setForm((prev) => {
      const alreadySelected = prev.categories.includes(categoryId);
      const nextCategories = alreadySelected
        ? prev.categories.filter((c) => c !== categoryId)
        : [...prev.categories, categoryId];

      // Auto-populate starter services so the provider doesn't type from
      // scratch. Templates are stamped with `_template_id` + `_from_template`
      // so we can untangle them later, the provider can untick or edit
      // anything before submission.
      let nextServices = prev.draft_services || [];
      if (alreadySelected) {
        // Category just unticked, remove every template entry that came
        // from this category AND wasn't manually edited by the provider.
        // We preserve `_edited: true` rows so a stylist who has already
        // tweaked the price doesn't lose their work.
        nextServices = nextServices.filter(
          (s) => !(s._from_template && s.category === categoryId && !s._edited),
        );
      } else {
        // Category just ticked, append every template for it that isn't
        // already present (by template_id). Re-ticking after a prune
        // restores the same set, no duplicates.
        const existingTemplateIds = new Set(
          nextServices.map((s) => s._template_id).filter(Boolean),
        );
        const additions = (SERVICE_TEMPLATES[categoryId] || [])
          .filter((t) => !existingTemplateIds.has(t.template_id))
          .map((t) => ({
            name: t.name,
            description: '',
            duration: t.duration,
            price: t.price,
            category: categoryId,
            service_types: prev.service_types || [],
            _template_id: t.template_id,
            _from_template: true,
            _edited: false,
          }));
        nextServices = [...nextServices, ...additions];
      }

      return { ...prev, categories: nextCategories, draft_services: nextServices };
    });
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    try {
      const { _profileFile, profile_image, cancellation_policy, ...draftData } = form;
      draftData.cancellation_policy = { type: cancellation_policy, custom_note: '' };
      // Persist exactly where the user left off so resume lands here next
      // time, rather than relying on data-completeness heuristics.
      draftData.onboarding_step = step;
      await api.post('/providers/save-draft', draftData);
      if (_profileFile) {
        const imgData = new FormData();
        imgData.append('file', _profileFile);
        await api.post('/providers/me/profile-image', imgData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      toast.success('Draft saved');
      navigate('/provider/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to save draft');
    } finally { setSaving(false); }
  };

  // Strip frontend-only template tracking fields before sending the
  // final payload to /providers/onboard. The Pydantic DraftService
  // model only accepts {name, description, duration, price, category,
  // service_types}, so underscore-prefixed metadata gets pruned here.
  // `save-draft` is a passthrough and intentionally preserves them so a
  // partially-completed onboarding resumes with the same template
  // bookkeeping (untick/edit state survives a browser refresh).
  const cleanDraftServicesForSubmit = (list) => (list || []).map((s) => ({
    name: s.name,
    description: s.description || '',
    duration: s.duration,
    price: typeof s.price === 'number' ? s.price : parseFloat(s.price) || 0,
    category: s.category,
    service_types: s.service_types || [],
  }));

  const handleSubmit = async () => {
    if (!form.brand_name || !form.username || !form.bio || !form.location || form.categories.length === 0 || form.service_types.length === 0) {
      toast.error('Please fill in all required fields'); return;
    }
    if (!form.postcode || form.postcode.trim().length < 3) {
      toast.error('Please enter your postcode'); return;
    }
    if (usernameStatus !== 'available') {
      toast.error('Please choose an available username'); return;
    }
    if (form.service_types.includes('salon') && (!form.salon_address || !form.salon_address.line1)) {
      toast.error('Please provide your salon/studio address'); return;
    }
    if (form.service_types.includes('home') && (!form.home_address || !form.home_address.line1)) {
      toast.error('Please provide your home address'); return;
    }
    if (form.service_types.includes('mobile') && !form.service_radius) {
      toast.error('Please select your travel radius'); return;
    }
    if (!Object.values(form.social_links).some(v => v && v.trim())) {
      toast.error('Please add at least one social media link'); return;
    }

    setLoading(true);
    try {
      // Save full profile first via onboard (strict validation)
      const { _profileFile, profile_image, cancellation_policy, ...onboardData } = form;
      onboardData.cancellation_policy = { type: cancellation_policy, custom_note: '' };
      onboardData.draft_services = cleanDraftServicesForSubmit(form.draft_services);
      await api.post('/providers/onboard', onboardData);
      if (_profileFile) {
        const imgData = new FormData();
        imgData.append('file', _profileFile);
        await api.post('/providers/me/profile-image', imgData, { headers: { 'Content-Type': 'multipart/form-data' } });
      }
      // Then submit for review
      await api.post('/providers/submit');
      toast.success('Profile submitted for review!');
      navigate('/provider/dashboard');
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(detail || 'Failed to submit. Please check all fields are filled in correctly.');
    } finally { setLoading(false); }
  };

  const isNextDisabled = () => {
    if (step === 1) return !form.brand_name || !form.username || usernameStatus !== 'available' || !form.bio || !form.location || !Object.values(form.social_links).some(v => v && v.trim());
    if (step === 2) return form.service_types.length === 0 || (form.service_types.includes('salon') && !form.salon_address?.line1) || (form.service_types.includes('home') && !form.home_address?.line1) || (form.service_types.includes('mobile') && !form.service_radius);
    if (step === 3) return false; // Deposit step is optional
    if (step === 4) return form.categories.length === 0;
    if (step === 5) return false; // Services step is optional
    if (step === 6) return false; // Payouts step is optional (but strongly encouraged)
    return false;
  };

  // Skip-modal handlers, triggered when a provider clicks Next on step 6
  // without having connected their bank. Evidence-backed friction.
  const handleFinishStripeOnboarding = async () => {
    setStartingConnect(true);
    try {
      const res = await api.post('/providers/me/stripe-connect/onboard', null, {
        params: { origin: 'onboarding' },
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
        return;
      }
      toast.error('Could not generate onboarding link');
    } catch (err) {
      toast.error(stripeErrorToast(err, 'Could not start Stripe onboarding'));
    } finally {
      setStartingConnect(false);
    }
  };

  const handleSkipPayout = () => {
    setSkipPayoutOpen(false);
    setStep(7);
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <Header />
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <StepProgress step={step} steps={STEPS} />

        <div className="card-surface p-4 sm:p-8">
          {step === 1 && <BasicInfoStep form={form} setForm={setForm} usernameStatus={usernameStatus} />}
          {step === 2 && <ServiceTypeStep form={form} setForm={setForm} onServiceTypeToggle={handleServiceTypeToggle} />}
          {step === 3 && <DepositStep form={form} setForm={setForm} />}
          {step === 4 && <CategoriesStep form={form} setForm={setForm} onCategoryToggle={handleCategoryToggle} />}
          {step === 5 && <ServicesStep form={form} setForm={setForm} />}
          {step === 6 && <PayoutStep onStatusChange={setPayoutStatus} />}
          {step === 7 && <ReviewStep form={form} />}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-[#E6E4DD]">
            <div>
              {step > 1 && (
                <Button variant="outline" onClick={() => setStep(step - 1)} className="border-[#E6E4DD]">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              )}
            </div>
            
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={handleSaveDraft}
                disabled={saving}
                className="border-[#E6E4DD]"
                data-testid="save-draft-btn"
              >
                <Save className="h-4 w-4 mr-2" />
                {saving ? 'Saving...' : 'Save Draft'}
              </Button>
              
              {step < 7 ? (
                <Button 
                  onClick={() => {
                    // Payouts step: if they haven't connected yet, surface a
                    // gentle friction modal so they don't silently skip.
                    // Pending/enabled states are safe to advance past without prompting.
                    if (step === 6 && payoutStatus &&
                        ['not_connected', 'action_required'].includes(payoutStatus.status)) {
                      setSkipPayoutOpen(true);
                      return;
                    }
                    // Auto-persist before advancing so refresh/close at any
                    // point resumes where the user left off, not one step
                    // behind. Fire-and-forget: never blocks the UX.
                    const nextStep = step + 1;
                    (async () => {
                      try {
                        const { _profileFile, profile_image, cancellation_policy, ...draftData } = form;
                        draftData.cancellation_policy = { type: cancellation_policy, custom_note: '' };
                        draftData.onboarding_step = nextStep;
                        await api.post('/providers/save-draft', draftData);
                      } catch { /* silent, Save Draft button remains as fallback */ }
                    })();
                    setStep(nextStep);
                  }}
                  className="btn-primary"
                  disabled={isNextDisabled()}
                  data-testid="next-step-btn"
                >
                  {step === 5 ? ((form.draft_services || []).length === 0 ? 'Skip' : 'Next') : 'Next'}
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              ) : (
                <Button 
                  onClick={handleSubmit}
                  disabled={loading || form.categories.length === 0 || form.service_types.length === 0}
                  className="btn-primary"
                  data-testid="submit-profile-btn"
                >
                  {loading ? 'Submitting...' : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Submit for Review
                    </>
                  )}
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Skip-for-now confirmation modal, Payouts step friction guard. */}
      <AlertDialog open={skipPayoutOpen} onOpenChange={setSkipPayoutOpen}>
        <AlertDialogContent data-testid="skip-payout-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-amber-500" />
              Skip for now?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-stone-600 leading-relaxed">
              Just a reminder, your first payout won't run until you connect.
              You can do it in 30 seconds from your Earnings tab when you're ready.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-2">
            <AlertDialogCancel
              onClick={handleSkipPayout}
              className="order-2 sm:order-1"
              data-testid="skip-payout-skip-anyway"
            >
              Skip anyway
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleFinishStripeOnboarding}
              disabled={startingConnect}
              className="bg-[#D4AF37] hover:bg-[#C49E2F] gap-2 order-1 sm:order-2"
              data-testid="skip-payout-finish-onboarding"
            >
              {startingConnect
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : <ExternalLink className="h-4 w-4" />}
              {startingConnect ? 'Opening Stripe...' : 'Finish onboarding'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
